import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HiOutlineCheck, HiOutlineMagnifyingGlass, HiOutlinePlus } from 'react-icons/hi2';

import StepChip from './step-chip';

/**
 * A chip in the row that opens a searchable list when clicked.
 *
 * Used wherever a step needs the author to choose something - the variable an
 * answer goes into, the function a RUN step calls - so the choice is made in
 * place rather than in a panel elsewhere on the screen.
 *
 * `onCreate`, when given, adds a "create new" row at the bottom of the list;
 * that is how a variable that does not exist yet gets made without leaving the
 * row.
 */
export default function InlinePicker({
    value,
    options = [],
    onChange,
    onCreate,
    createLabel = 'Create new',
    placeholder = 'choose',
    tone = 'variable',
    prefix,
    searchable = true,
    trigger,
    ariaLabel,
    align = 'start',
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    useEffect(() => { if (!open) setQuery(''); }, [open]);

    const normalised = useMemo(
        () => options.map(o => (typeof o === 'string' ? { value: o, label: o } : o)),
        [options],
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return normalised;
        return normalised.filter(o => String(o.label).toLowerCase().includes(q));
    }, [normalised, query]);

    const selected = normalised.find(o => o.value === value);

    return (
        <span className="relative inline-flex" ref={ref} onKeyDown={e => {
            if (e.key === 'Escape' && open) {
                e.stopPropagation();
                setOpen(false);
                ref.current?.querySelector('button')?.focus();
            }
        }}>
            <button
                type="button"
                aria-label={ariaLabel}
                aria-expanded={open}
                onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
                onMouseDown={e => e.stopPropagation()}
                className="max-w-full rounded-md transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
                {trigger || (value ? (
                    <StepChip tone={tone} prefix={prefix} value={selected?.label || value} mono={tone === 'variable'} />
                ) : (
                    <span className="inline-flex items-center rounded-md border border-dashed border-gray-300 px-2 py-0.5 text-[12.5px] text-gray-400 hover:border-gray-400 hover:text-gray-500">
                        {placeholder}
                    </span>
                ))}
            </button>

            {open && (
                <div
                    onClick={e => e.stopPropagation()}
                    className={`absolute ${align === 'end' ? 'right-0' : 'left-0'} top-full z-30 mt-1 w-64 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg ring-1 ring-black/[0.03]`}
                >
                    {searchable && normalised.length > 6 && (
                        <div className="flex items-center gap-1.5 border-b border-gray-100 px-2.5 py-2">
                            <HiOutlineMagnifyingGlass size={13} className="shrink-0 text-gray-400" />
                            <input
                                autoFocus
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder="Search"
                                className="w-full border-0 p-0 text-[13px] placeholder:text-gray-400 focus:ring-0"
                            />
                        </div>
                    )}

                    <div className="max-h-56 overflow-y-auto p-1">
                        {filtered.length === 0 && (
                            <div className="px-2.5 py-3 text-center text-[12.5px] text-gray-400">
                                Nothing matches
                            </div>
                        )}

                        {filtered.map(option => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => { onChange(option.value); setOpen(false); }}
                                className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] text-gray-700 transition-colors hover:bg-gray-50"
                            >
                                <span className="truncate">{option.label}</span>
                                {option.value === value && (
                                    <HiOutlineCheck size={14} className="shrink-0 text-indigo-600" />
                                )}
                            </button>
                        ))}
                    </div>

                    {onCreate && (
                        <button
                            type="button"
                            onClick={() => { setOpen(false); onCreate(); }}
                            className="flex w-full items-center gap-1.5 border-t border-gray-100 px-3 py-2 text-left text-[13px] font-medium text-indigo-600 transition-colors hover:bg-indigo-50"
                        >
                            <HiOutlinePlus size={13} /> {createLabel}
                        </button>
                    )}
                </div>
            )}
        </span>
    );
}
